import { useCallback, useState } from 'react'

import { isAddress } from 'viem'
import { mainnet } from 'viem/chains'

import { hasENSDomain, useLookupENS } from '~/hooks/useENS'
import { IAddress } from '~/types'
import { EvmAddress } from '~/utils/address'

interface ResolvedRecipient {
  address: IAddress
  amount: string
}

const getAmountRegex = (decimals: number) => {
  if (decimals === 0) {
    return new RegExp(`^\\d+$`)
  }
  return new RegExp(`^\\d+(\\.\\d{1,${decimals}})?$`)
}
const MAX_RECIPIENTS = 100

/**
 * Parse, validate, and resolve recipients from CSV input
 * Handles both validation and ENS resolution in a single function
 *
 * @param recipientsString - CSV string with format: "address/ENS, amount" per line
 * @param lookupENS - Function to resolve ENS to address
 * @returns Array of resolved recipients with checksummed addresses
 * @throws Error with descriptive message if validation or resolution fails
 */
export const parseAndResolveRecipients = async (
  recipientsString: string,
  lookupENS: ReturnType<typeof useLookupENS>,
  decimals: number,
): Promise<ResolvedRecipient[]> => {
  if (!recipientsString || recipientsString.trim().length === 0) {
    throw new Error('Recipients cannot be empty')
  }

  const lines = recipientsString
    .split('\n')
    .filter((line) => line.trim().length > 0)

  if (lines.length === 0) {
    throw new Error('At least one recipient is required')
  }

  if (lines.length > MAX_RECIPIENTS) {
    throw new Error(`Maximum of ${MAX_RECIPIENTS} recipients allowed`)
  }

  const parsedRecipients = lines.map((line, lineIndex) => {
    const lineNumber = lineIndex + 1
    const trimmed = line.trim()
    const parts = trimmed.split(',').map((part) => part.trim())

    // Must have exactly 2 parts
    if (parts.length !== 2) {
      throw new Error(
        `Line ${lineNumber}: Must be in CSV format (address or ENS, amount)`,
      )
    }

    const [addressOrEns, amountStr] = parts

    // Validate address or ENS format
    if (!isAddress(addressOrEns) && !hasENSDomain(addressOrEns)) {
      throw new Error(
        `Line ${lineNumber}: "${addressOrEns}" is not a valid Ethereum address or ENS domain`,
      )
    }

    // Validate amount format
    if (!getAmountRegex(decimals).test(amountStr)) {
      throw new Error(
        `Line ${lineNumber}: Amount "${amountStr}" must be a number with at most ${decimals} decimal places`,
      )
    }

    // Validate amount value
    const amount = parseFloat(amountStr)
    if (isNaN(amount) || amount <= 0) {
      throw new Error(`Line ${lineNumber}: Amount must be a positive number`)
    }

    return { addressOrEns, amount: amountStr, lineNumber }
  })

  // Resolve all ENS domains in parallel
  const resolutionPromises = parsedRecipients.map(
    async ({ addressOrEns, amount, lineNumber }) => {
      // If it's already an address, just checksum it
      if (isAddress(addressOrEns)) {
        return {
          address: EvmAddress.format(addressOrEns),
          amount,
          lineNumber,
        }
      }

      // If it's an ENS domain, resolve it
      if (hasENSDomain(addressOrEns)) {
        const resolvedAddress = await lookupENS(addressOrEns, mainnet.id)

        if (!resolvedAddress) {
          throw new Error(
            `Line ${lineNumber}: Could not resolve ENS domain: ${addressOrEns}`,
          )
        }

        return {
          address: resolvedAddress,
          amount,
          lineNumber,
        }
      }

      // This should never happen due to validation above
      throw new Error(`Line ${lineNumber}: Invalid input: ${addressOrEns}`)
    },
  )

  const resolvedRecipientsWithLineNumbers =
    await Promise.all(resolutionPromises)

  // Check for duplicate addresses
  const addressToLines = new Map<string, number[]>()
  resolvedRecipientsWithLineNumbers.forEach((recipient) => {
    const lines = addressToLines.get(recipient.address) || []
    lines.push(recipient.lineNumber)
    addressToLines.set(recipient.address, lines)
  })

  const duplicates = Array.from(addressToLines.entries()).filter(
    ([, lines]) => lines.length > 1,
  )

  if (duplicates.length > 0) {
    const duplicateMessages = duplicates.map(([address, lines]) => {
      const lineNumbers = lines.sort((a, b) => a - b).join(', ')
      return `Address ${address} appears on lines ${lineNumbers}`
    })
    throw new Error(
      `Duplicate addresses found:\n${duplicateMessages.join('\n')}`,
    )
  }

  // Remove lineNumber from the returned objects
  return resolvedRecipientsWithLineNumbers.map((recipient) => ({
    address: recipient.address,
    amount: recipient.amount,
  }))
}

export const useMultiRecipientValidation = ({
  onError,
}: {
  onError: (error: Error) => void
}) => {
  const lookupENS = useLookupENS()
  const [isLoading, setIsLoading] = useState(false)

  const validateRecipients = useCallback(
    async (
      recipientsString: string,
      decimals: number,
    ): Promise<ResolvedRecipient[] | undefined> => {
      setIsLoading(true)
      try {
        return await parseAndResolveRecipients(
          recipientsString,
          lookupENS,
          decimals,
        )
      } catch (error) {
        onError(error as Error)
      } finally {
        setIsLoading(false)
      }
    },
    [lookupENS, onError],
  )

  return {
    validateRecipients,
    isLoading,
  }
}
